import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { IonPage, IonContent } from "@ionic/react";
import { Check, Flame, ListPlus, Plus, Sparkles, Trophy, X } from "lucide-react";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { PrimaryButton, Button } from "../components/ui/Button";
import { RoomIcon } from "../components/ui/RoomIcon";
import { cn } from "../lib/utils";
import { useAuth } from "../hooks/useAuthStore";
import { useStore } from "../store/useStore";
import { useToast } from "../hooks/useToast";
import { ZError } from "../types/zod-schemas";

type OnboardingStep = "household" | "areas" | "done";
type HouseholdMode = "create" | "join";

const SAMPLE_AREAS = [
  { name: "Kitchen" },
  { name: "Bathroom" },
  { name: "Living Room" },
  { name: "Bedroom" },
  { name: "Car" },
  { name: "Laundry" },
];

const STEP_TITLES: Record<OnboardingStep, string> = {
  household: "Get Started",
  areas: "Choose Areas",
  done: "You're All Set",
};

const TOTAL_STEPS = 3;

function stepIndex(step: OnboardingStep): number {
  const order: OnboardingStep[] = ["household", "areas", "done"];
  return order.indexOf(step) + 1;
}

export default function OnboardingPage() {
  const history = useHistory();
  const { isAuthenticated } = useAuth();
  const showToast = useToast((s) => s.showToast);

  const {
    userHouseholds,
    areas: existingAreas,
    loading,
    createHousehold,
    joinHousehold,
    createArea,
  } = useStore();

  const [currentStep, setCurrentStep] = useState<OnboardingStep>("household");
  const [householdMode, setHouseholdMode] = useState<HouseholdMode>("create");
  const [householdName, setHouseholdName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [customAreaName, setCustomAreaName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Auth guard — redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      history.replace("/login");
    }
  }, [isAuthenticated, history]);

  // Determine starting step based on existing data
  useEffect(() => {
    if (loading.households || loading.areas || isInitialized) return;

    // If user already has a household and areas, onboarding is done
    if (userHouseholds.length > 0 && existingAreas.length > 0) {
      history.replace("/");
      return;
    }

    // If user has a household but no areas, jump to areas step
    if (userHouseholds.length > 0) {
      setCurrentStep("areas");
    }

    setIsInitialized(true);
  }, [
    userHouseholds,
    existingAreas,
    loading.households,
    loading.areas,
    isInitialized,
    history,
  ]);

  const handleCreateHousehold = async () => {
    if (!householdName.trim()) return;
    setIsSubmitting(true);
    try {
      const result = await createHousehold(householdName.trim());
      if (!result) {
        throw new Error("Failed to create household");
      }
      showToast("Household created!", "success");
      setCurrentStep("areas");
    } catch (error) {
      console.error("Error creating household:", error);
      let message = "Failed to create household. Please try again.";
      try {
        const parsed = ZError.parse(error);
        message = parsed.message;
      } catch {
        // use default
      }
      showToast(message, "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJoinHousehold = async () => {
    if (!inviteCode.trim()) return;
    setIsSubmitting(true);
    try {
      const result = await joinHousehold(inviteCode.trim());
      if (!result) {
        throw new Error("Failed to join household");
      }
      showToast("Joined household!", "success");
      // Skip areas step — the household already has areas
      setCurrentStep("done");
    } catch (error) {
      console.error("Error joining household:", error);
      let message = "Invalid invite code. Please check and try again.";
      try {
        const parsed = ZError.parse(error);
        message = parsed.message;
      } catch {
        // use default
      }
      showToast(message, "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleArea = (areaName: string) => {
    setSelectedAreas((prev) =>
      prev.includes(areaName)
        ? prev.filter((n) => n !== areaName)
        : [...prev, areaName]
    );
  };

  const handleAddCustomArea = () => {
    const name = customAreaName.trim();
    if (!name) return;
    if (!selectedAreas.includes(name)) {
      setSelectedAreas((prev) => [...prev, name]);
    }
    setCustomAreaName("");
  };

  const handleCreateAreas = async () => {
    if (selectedAreas.length === 0) {
      setCurrentStep("done");
      return;
    }
    setIsSubmitting(true);
    try {
      for (const areaName of selectedAreas) {
        await createArea(areaName);
      }
      showToast(
        `${selectedAreas.length} area${selectedAreas.length > 1 ? "s" : ""} created!`,
        "success"
      );
      setCurrentStep("done");
    } catch (error) {
      console.error("Error creating areas:", error);
      showToast("Failed to create areas. Please try again.", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoToDashboard = () => {
    history.replace("/");
  };

  // Show themed spinner while determining start step
  if (!isInitialized || loading.households) {
    return <LoadingSpinner message="Getting things ready..." />;
  }

  const progress = (stepIndex(currentStep) / TOTAL_STEPS) * 100;

  return (
    <IonPage>
      <IonContent>
        <div className="min-h-full bg-background pb-24 pt-[env(safe-area-inset-top)]">
          <div className="max-w-lg mx-auto px-5 pt-5">
            {/* Step header + progress */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {STEP_TITLES[currentStep]}
              </span>
              <span className="text-xs font-bold text-muted-foreground tabular-nums">
                Step {stepIndex(currentStep)} of {TOTAL_STEPS}
              </span>
            </div>
            <div
              className="h-2 rounded-full bg-surface-raised overflow-hidden mb-8"
              role="progressbar"
              aria-label="Onboarding progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress)}
            >
              <div
                className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Step 1: Household */}
            {currentStep === "household" && (
              <div className="animate-fade-in">
                <div className="text-center mb-8">
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-gradient-to-br from-sky-400 to-sky-600 text-white shadow-glow-cyan mb-5"
                  >
                    <Sparkles className="w-8 h-8" />
                  </span>
                  <h1 className="text-[28px] leading-tight font-extrabold tracking-tight text-foreground mb-2">
                    Welcome to Tidy
                  </h1>
                  <p className="text-muted-foreground">
                    Create a new household or join an existing one to get
                    started.
                  </p>
                </div>

                {/* Mode toggle */}
                <div className="flex p-1 rounded-2xl bg-surface-raised mb-5" role="group" aria-label="Household setup mode">
                  {(["create", "join"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setHouseholdMode(mode)}
                      aria-pressed={householdMode === mode}
                      className={cn(
                        "flex-1 min-h-[44px] rounded-xl text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        householdMode === mode
                          ? "bg-card text-foreground shadow-soft"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {mode === "create" ? "Create Household" : "Join Household"}
                    </button>
                  ))}
                </div>

                <div className="card-surface p-5">
                  {householdMode === "create" ? (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="onboarding-household-name" className="field-label">
                          Household Name
                        </label>
                        <input
                          id="onboarding-household-name"
                          type="text"
                          value={householdName}
                          onChange={(e) => setHouseholdName(e.target.value)}
                          placeholder="The Smith Family, Apt 4B..."
                          className="input-field"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && householdName.trim()) {
                              handleCreateHousehold();
                            }
                          }}
                        />
                      </div>
                      <PrimaryButton
                        onClick={handleCreateHousehold}
                        disabled={!householdName.trim()}
                        loading={isSubmitting}
                      >
                        {isSubmitting ? "Creating..." : "Create Household"}
                      </PrimaryButton>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="onboarding-invite-code" className="field-label">
                          Invite Code
                        </label>
                        <input
                          id="onboarding-invite-code"
                          type="text"
                          value={inviteCode}
                          onChange={(e) => setInviteCode(e.target.value)}
                          placeholder="Enter invite code..."
                          autoCapitalize="characters"
                          autoComplete="off"
                          className="input-field font-mono tracking-wider"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && inviteCode.trim()) {
                              handleJoinHousehold();
                            }
                          }}
                        />
                      </div>
                      <PrimaryButton
                        onClick={handleJoinHousehold}
                        disabled={!inviteCode.trim()}
                        loading={isSubmitting}
                      >
                        {isSubmitting ? "Joining..." : "Join Household"}
                      </PrimaryButton>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Areas */}
            {currentStep === "areas" && (
              <div className="animate-fade-in">
                <div className="mb-6">
                  <h1 className="text-[28px] leading-tight font-extrabold tracking-tight text-foreground mb-2">
                    Which areas do you want to keep tidy?
                  </h1>
                  <p className="text-muted-foreground">
                    Select the spaces you'd like to manage. You can always add
                    more later.
                  </p>
                </div>

                {/* Area grid */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {SAMPLE_AREAS.map((area) => {
                    const selected = selectedAreas.includes(area.name);
                    return (
                      <button
                        key={area.name}
                        type="button"
                        onClick={() => toggleArea(area.name)}
                        aria-pressed={selected}
                        className={cn(
                          "relative flex flex-col items-start gap-3 p-4 rounded-3xl border-2 text-left transition-all min-h-[104px]",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                          selected
                            ? "border-primary bg-accent"
                            : "border-border bg-card hover:border-primary/40"
                        )}
                      >
                        <RoomIcon name={area.name} />
                        <span className="font-bold text-foreground text-sm">
                          {area.name}
                        </span>
                        {selected && (
                          <span
                            aria-hidden="true"
                            className="absolute top-3 right-3 inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground"
                          >
                            <Check className="w-4 h-4" strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Custom area input */}
                <div className="card-surface p-4 mb-5">
                  <label htmlFor="onboarding-custom-area" className="field-label">
                    Add a custom area
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="onboarding-custom-area"
                      type="text"
                      value={customAreaName}
                      onChange={(e) => setCustomAreaName(e.target.value)}
                      placeholder="Garden, Office..."
                      className="input-field"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && customAreaName.trim()) {
                          handleAddCustomArea();
                        }
                      }}
                    />
                    <Button
                      variant="soft"
                      onClick={handleAddCustomArea}
                      disabled={!customAreaName.trim()}
                      leadingIcon={<Plus className="w-4 h-4" aria-hidden="true" />}
                      className="min-h-[48px]"
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {/* Selected summary */}
                {selectedAreas.length > 0 && (
                  <ul className="flex flex-wrap gap-2 mb-6" aria-label="Selected areas">
                    {selectedAreas.map((name) => (
                      <li key={name}>
                        <button
                          type="button"
                          onClick={() => toggleArea(name)}
                          aria-label={`Remove ${name}`}
                          className="inline-flex items-center gap-1.5 min-h-[36px] rounded-full bg-accent text-accent-foreground pl-1.5 pr-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <RoomIcon name={name} size="sm" className="w-7 h-7 rounded-full" />
                          {name}
                          <X className="w-3.5 h-3.5" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <PrimaryButton
                  onClick={handleCreateAreas}
                  loading={isSubmitting}
                >
                  {isSubmitting
                    ? "Creating..."
                    : selectedAreas.length > 0
                      ? "Create Areas"
                      : "Skip for Now"}
                </PrimaryButton>
              </div>
            )}

            {/* Step 3: Done */}
            {currentStep === "done" && (
              <div className="animate-fade-in">
                <div className="text-center mb-8">
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-20 h-20 rounded-[28px] bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-lg mb-5 animate-pop-in"
                  >
                    <Check className="w-10 h-10" strokeWidth={3} />
                  </span>
                  <h1 className="text-[28px] leading-tight font-extrabold tracking-tight text-foreground mb-2">
                    You're all set!
                  </h1>
                  <p className="text-muted-foreground">
                    Here are a few tips to get started.
                  </p>
                </div>

                <ol className="card-surface p-2 mb-8">
                  {[
                    {
                      icon: <ListPlus className="w-5 h-5" />,
                      tone: "bg-accent text-accent-foreground",
                      text: "Add tasks to your areas and complete them to earn points.",
                    },
                    {
                      icon: <Flame className="w-5 h-5" />,
                      tone: "bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300",
                      text: "Build daily streaks by staying consistent with your cleaning routine.",
                    },
                    {
                      icon: <Trophy className="w-5 h-5" />,
                      tone: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
                      text: "Invite family members from Settings to compete on the leaderboard.",
                    },
                  ].map((tip) => (
                    <li key={tip.text} className="flex items-start gap-3 p-3">
                      <span
                        aria-hidden="true"
                        className={cn(
                          "inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0",
                          tip.tone
                        )}
                      >
                        {tip.icon}
                      </span>
                      <p className="text-foreground text-sm pt-2.5">{tip.text}</p>
                    </li>
                  ))}
                </ol>

                <PrimaryButton onClick={handleGoToDashboard}>
                  Go to Dashboard
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
